import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Table,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { RotateCcw, Search } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { catchError, EMPTY, tap } from 'rxjs';
import LoadingSpin from '../../../../components/loading-spin';
import SyncSuspense from '../../../../components/sync-suspense';
import { type CycleMenuResponseDto } from '../../../../dto/cycle-menu/cycle-menu-response-dto';
import { type CycleMenu, CycleMenuSupply, Supply } from '../../../../entities';
import { useNotification } from '../../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../../jsonapi-provider';
import utils from '../../../../utils/utils';

interface PropsType {
  cycleMenu: CycleMenuResponseDto;
}

const AssignedSuppliesPage = (props: PropsType): ReactNode => {
  const { token } = theme.useToken();
  const notification = useNotification();
  const atomicOperations = useAtomicOperations();
  const [formInstance] = Form.useForm();
  const { cycleMenu } = props;
  const [searchText, setSearchText] = useState('');

  const supplies = useQuery<Supply[]>(['supply', { sort: 'name', page: { limit: 10000 } }]);
  const cycleMenuQuery = useQuery<CycleMenu>(
    cycleMenu?.id
      ? [
          'cycle-menu',
          String(cycleMenu.id),
          {
            include: ['cycleMenuSupplies.supply'],
            fields: {
              'cycle-menu': ['cycleMenuSupplies'],
              'cycle-menu-supply': ['supply', 'price'],
              supply: ['name', 'price'],
            },
          },
        ]
      : null
  );

  const suppliesData =
    Form.useWatch(['supplies'], { form: formInstance, preserve: true }) ??
    formInstance.getFieldValue('supplies') ??
    [];

  useEffect(() => {
    if (!cycleMenu?.id || !supplies.data) return;
    const preparedValues: any[] = supplies.data?.map((s) => ({
      price: s.price,
      name: s.name,
      id: s.id,
      defaultPrice: s.price,
      canReset: false,
    }));
    const assignedSupplies = cycleMenuQuery.data?.cycleMenuSupplies ?? [];
    assignedSupplies.forEach((scp) => {
      const supplyPrice = preparedValues.find((s: any) => s.id === scp.supply.id);
      if (supplyPrice) {
        supplyPrice.price = scp.price;
        supplyPrice.childPrice = scp.price;
        supplyPrice.canReset = true;
        supplyPrice.cmsId = scp.id;
      }
    });
    formInstance.setFieldValue('supplies', preparedValues);
  }, [cycleMenu.id, supplies.data, cycleMenuQuery.data]);

  const getSuppliesOperations = (suppliesToBeSubmitted: any): any => {
    return suppliesToBeSubmitted
      .map((stbs: any) => {
        if (
          stbs.cmsId !== undefined &&
          ((stbs.parentPrice !== undefined && stbs.parentPrice === stbs.price) ||
            (stbs.parentPrice === undefined && stbs.price === stbs.defaultPrice))
        ) {
          return AtomicOperation.remove([
            'cycle-menu',
            String(cycleMenu.id),
            'cycleMenuSupplies',
            stbs.cmsId,
          ]);
        } else if (
          stbs.cmsId !== undefined &&
          ((stbs.parentPrice !== undefined && stbs.parentPrice !== stbs.price) ||
            (stbs.parentPrice === undefined && stbs.price !== stbs.defaultPrice))
        ) {
          return AtomicOperation.update(
            new CycleMenuSupply(stbs.cmsId!, stbs.price, new Supply(stbs.id)),
            undefined,
            ['cycle-menu', String(cycleMenu.id), 'cycleMenuSupplies', stbs.cmsId]
          );
        } else if (
          stbs.cmsId === undefined &&
          ((stbs.parentPrice !== undefined && stbs.parentPrice !== stbs.price) ||
            (stbs.parentPrice === undefined && stbs.price !== stbs.defaultPrice))
        ) {
          return AtomicOperation.add(
            new CycleMenuSupply(undefined, stbs.price, new Supply(stbs.id)),
            undefined,
            ['cycle-menu', String(cycleMenu.id), 'cycleMenuSupplies']
          );
        } else {
          return undefined;
        }
      })
      .filter((el: any) => el !== undefined);
  };

  const onSave = (): void => {
    const suppliesOperations = getSuppliesOperations(suppliesData);
    if (suppliesOperations.length === 0) {
      notification.info({ message: '', description: 'No changes detected to save' });
      return;
    }
    atomicOperations
      .operate(suppliesOperations)
      .pipe(
        tap(() => {
          cycleMenuQuery.refetch?.();
          notification.success({
            message: '',
            description: 'Supplies updated successfully',
          });
        }),
        catchError((error: any) => {
          notification.error({
            message: error?.title ?? '',
            description:
              error?.detail ??
              error?.message ??
              (typeof error === 'string' ? error : 'Failed to update supplies'),
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  const onDiscard = (): void => {
    cycleMenuQuery.refetch?.();
    notification.info({ message: '', description: 'Changes discarded' });
  };

  const columns = [
    {
      key: 'action',
      width: 30,
      render: (_: any, record: any) =>
        record.canReset ? (
          <Tooltip
            title={`Reset to ${`the default price ${utils.currencyFormat(record.defaultPrice)}`}`}
          >
            <Button
              shape="circle"
              size="small"
              type="text"
              style={{ color: token.colorInfo }}
              icon={<RotateCcw />}
              onClick={() => {
                formInstance.setFieldValue(
                  [
                    'supplies',
                    formInstance
                      .getFieldValue('supplies')
                      .findIndex((el: any) => el.id === record.id),
                  ],
                  { ...record, price: record.defaultPrice, canReset: false }
                );
              }}
            />
          </Tooltip>
        ) : (
          <></>
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '65%',
      filterSearch: true,
      onFilter: (value: any, record: any) => record.name.startsWith(value as string),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: '30%',
      render: (_: any, record: any) => {
        const itemIdx = suppliesData.findIndex((el: any) => el.id === record.id);
        const currentPrice = itemIdx >= 0 ? suppliesData[itemIdx]?.price : record.price;
        return (
          <InputNumber
            precision={2}
            style={{ width: '100%', maxWidth: 100 }}
            min={0}
            value={currentPrice}
            formatter={(v) => utils.currencyFormat(Number(v) ?? 0)}
            onChange={(val) => {
              if (itemIdx >= 0) {
                formInstance.setFieldValue(['supplies', itemIdx], {
                  ...record,
                  price: val,
                  canReset: val !== record.defaultPrice,
                });
              }
            }}
          />
        );
      },
    },
  ];

  const filteredData = suppliesData.filter(
    (item: Supply) => item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Form form={formInstance} component={false}>
      <Flex gap="middle" vertical>
        <Flex gap="middle" justify="space-between" align="center">
          <Flex gap="small">
            <Typography.Title level={3}>Supplies</Typography.Title>
          </Flex>
        </Flex>
        <SyncSuspense
          fallback={
            <Card style={{ marginBlockEnd: token.paddingXL * 6 }}>
              <LoadingSpin />
            </Card>
          }
          condition={!supplies.isLoading && !cycleMenuQuery.isLoading}
        >
          <Input
            placeholder="Search supplies..."
            prefix={<Search size={16} />}
            style={{ width: 300 }}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            allowClear
          />

          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 10 }}
            size="middle"
            style={{ marginBlockEnd: token.paddingXL * 6 }}
          />
        </SyncSuspense>

        <Flex
          className="floating"
          gap="small"
          style={{
            position: 'absolute',
            bottom: 0,
            flexDirection: 'row-reverse',
            width: `calc(100% - ${token.paddingMD * 2}px)`,
            paddingBlockStart: token.paddingXL * 2.5,
            paddingBlockEnd: token.paddingXL * 2,
            paddingInline: token.paddingXL,
            pointerEvents: 'none',
          }}
        >
          <Button
            size="large"
            type="primary"
            onClick={onSave}
            loading={atomicOperations.isLoading}
            disabled={atomicOperations.isLoading}
            style={{ pointerEvents: 'auto' }}
          >
            Save
          </Button>
          <Button
            size="large"
            onClick={onDiscard}
            disabled={atomicOperations.isLoading}
            style={{ pointerEvents: 'auto' }}
          >
            Discard Changes
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};

export default AssignedSuppliesPage;
